import { useCallback, useEffect, useState } from 'react';
import { CodeBlock } from 'react-code-blocks';
import { post } from 'src/api/requests';
import { Button } from 'src/components/Button';
import { Input } from 'src/components/Input';
import { Label } from 'src/components/Label';
import { Loading } from 'src/components/Loading';
import { Tab } from 'src/components/Tab';
import Table from 'src/components/Table';
import {
  Agent,
  getReferencedContextParams,
} from 'src/interfaces/agent.interface';
import { Number } from 'src/interfaces/number.interface';
import { isValidHttpUrl } from 'src/utils/url';
import { useNotification } from '../../../contexts/NotificationContext';
import { ListNumbers } from '../../Numbers/components/ListNumbers';

interface InboundCallsFormProps {
  setInboundCallActionButton: (element: React.ReactNode) => void;
  agentLoading: boolean;
  contextEndpoint: string;
  selectedNumbers: Number[];
  setSelectedNumbers: (numbers: Number[]) => void;
  setContextEndpoint: (endpoint: string) => void;
  updateAgentInboundSettings: (numbers?: Number[]) => Promise<void>;
  agent: Agent;
}

interface QueryParamsFormProps {
  data: Record<string, string>;
  onChange: (newData: Record<string, string>) => void;
  className?: string;
}

const QueryParamsForm: React.FC<QueryParamsFormProps> = ({
  data,
  onChange,
  className = '',
}) => {
  const [editableData, setEditableData] =
    useState<Record<string, string>>(data);

  useEffect(() => {
    setEditableData(data);
  }, [data]);

  const handleValueChange = (key: string, value: string) => {
    const newData = { ...editableData, [key]: value };
    setEditableData(newData);
    onChange(newData);
  };

  return (
    <div className={`bg-neutral-50 rounded-lg p-4 ${className}`}>
      <div className="space-y-3">
        {Object.entries(editableData).map(([key, value]) => (
          <div key={key} className="flex items-center gap-3">
            <div className="w-32 flex-shrink-0">
              <Label className="text-sm font-medium text-neutral-700">
                {key}
              </Label>
            </div>
            <div className="flex-1">
              <Input
                value={String(value)}
                onChange={(e) => handleValueChange(key, e.target.value)}
                className="text-sm"
                placeholder={`Enter ${key} value`}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const InboundCallsForm = ({
  setInboundCallActionButton,
  agentLoading,
  contextEndpoint,
  selectedNumbers,
  setSelectedNumbers,
  setContextEndpoint,
  updateAgentInboundSettings,
  agent,
}: InboundCallsFormProps) => {
  const notification = useNotification();
  const [preparedNumbers, setPreparedNumbers] = useState<Number[]>([]);
  const [tabIndex, setTabIndex] = useState(0);
  const [loadingContextEndpoint, setLoadingContextEndpoint] = useState(false);

  const [contextPayload, setContextPayload] = useState<Record<string, string>>({
    phone_number: '',
    to_phone: '',
    from_phone: '',
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [contextResponse, setContextResponse] = useState<any>(null);
  const [contextObject, setContextObject] = useState<Record<string, string>>(
    {},
  );

  useEffect(() => {
    setPreparedNumbers(selectedNumbers);
  }, [selectedNumbers]);

  const firstNumber = preparedNumbers.length > 0 ? preparedNumbers[0] : null;
  const agentNumber = firstNumber?.number || '';

  useEffect(() => {
    setContextPayload({
      phone_number: agentNumber,
      to_phone: agentNumber,
      from_phone: '',
    });
  }, [agentNumber]);

  const handleTestContextEndpoint = useCallback(async () => {
    setLoadingContextEndpoint(true);
    const context = {
      contextPayload,
      contextEndpoint: contextEndpoint,
    };
    try {
      const response = await post('/context/test', context);
      setContextResponse(response);
      notification.success('Context endpoint test succeeded');
    } catch (error) {
      setContextResponse(null);
      if ((error as { data: { message: string } }).data?.message) {
        notification.error(
          (error as { data: { message: string } }).data?.message,
        );
      } else {
        notification.error('An unexpected error occurred');
        console.error(error);
      }
    } finally {
      setLoadingContextEndpoint(false);
    }
  }, [contextEndpoint, contextPayload]);

  useEffect(() => {
    const referencedContextParams = getReferencedContextParams(agent);
    const updatedContextObj = referencedContextParams.reduce(
      (acc, key) => {
        acc[key] = contextObject[key]?.length > 0 ? contextObject[key] : '';
        return acc;
      },
      {} as Record<string, string>,
    );

    setContextObject(updatedContextObj);
  }, [agent.prompt, agent.initial_message, agent.voicemail_message]);

  useEffect(() => {
    setInboundCallActionButton(
      <>
        {' '}
        <Button
          onClick={async () => {
            setSelectedNumbers(preparedNumbers);
            await updateAgentInboundSettings(
              preparedNumbers.length > 0 ? preparedNumbers : undefined,
            );
          }}
          disabled={
            agentLoading ||
            (contextEndpoint.length > 0 && !isValidHttpUrl(contextEndpoint))
          }
          className={`${tabIndex === 1 ? 'hidden' : ''}`}
        >
          Save
        </Button>
        <Button
          onClick={handleTestContextEndpoint}
          disabled={agentLoading || loadingContextEndpoint}
          className={`${tabIndex === 0 ? 'hidden' : ''}`}
        >
          Test Context Endpoint
        </Button>
      </>,
    );
  }, [handleTestContextEndpoint]);

  return (
    <>
      {agentLoading && (
        <div className="flex justify-center items-center h-20">
          <Loading size="8" />
        </div>
      )}

      {!agentLoading && (
        <Tab
          onChange={setTabIndex}
          items={[
            {
              label: 'Customize',
              content: (
                <>
                  <ListNumbers
                    key={selectedNumbers.length}
                    setSelectedNumbers={setPreparedNumbers}
                    selectedNumbers={preparedNumbers}
                    readOnly
                  />

                  <div className="mt-2">
                    <Label htmlFor="url">Context Endpoint</Label>
                    <Input
                      type="text"
                      id="url"
                      placeholder="https://example.com"
                      value={contextEndpoint}
                      onChange={(e) => setContextEndpoint(e.target.value)}
                      onBlur={() => {
                        if (!isValidHttpUrl(contextEndpoint)) {
                          setContextEndpoint('');
                        }
                      }}
                      disabled={agentLoading}
                      error={
                        !isValidHttpUrl(contextEndpoint) &&
                        contextEndpoint.length > 0
                          ? 'Please enter a valid URL'
                          : ''
                      }
                    />
                  </div>
                </>
              ),
            },
            ...(contextEndpoint.length > 0 && isValidHttpUrl(contextEndpoint)
              ? [
                  {
                    label: 'Test Context Endpoint',
                    content: (
                      <>
                        {loadingContextEndpoint && (
                          <div className="flex justify-center items-center h-20">
                            <Loading size="8" />
                          </div>
                        )}

                        {!loadingContextEndpoint && (
                          <div className="flex flex-col gap-2">
                            <span>
                              Context Endpoint Query Parameters (GET Request)
                            </span>
                            <QueryParamsForm
                              data={contextPayload}
                              onChange={setContextPayload}
                            />
                          </div>
                        )}

                        {!loadingContextEndpoint && contextResponse && (
                          <div className="flex flex-col gap-2 mt-6">
                            <span>Context Endpoint Response</span>
                            <CodeBlock
                              text={JSON.stringify(contextResponse, null, 2)}
                              language="json"
                              wrapLongLines={false}
                              showLineNumbers={false}
                            />
                          </div>
                        )}

                        {!loadingContextEndpoint && contextResponse && (
                          <div className="flex flex-col gap-2 mt-6">
                            <span className="text-lg font-semibold text-neutral-700">
                              Context Variables Comparison
                            </span>
                            <div className="bg-neutral-50 rounded-lg p-4">
                              <Table
                                headers={[
                                  {
                                    key: 'variable',
                                    label: 'Variable Name',
                                    width: '30%',
                                    align: 'left' as const,
                                  },
                                  {
                                    key: 'available',
                                    label: 'Available in Response',
                                    width: '25%',
                                    align: 'center' as const,
                                  },
                                  {
                                    key: 'responseValue',
                                    label: 'Value from Response',
                                    width: '45%',
                                    align: 'left' as const,
                                  },
                                ]}
                                rows={Object.keys(contextObject).map((key) => {
                                  const isAvailable =
                                    contextResponse &&
                                    typeof contextResponse === 'object' &&
                                    key in contextResponse;
                                  const responseValue = isAvailable
                                    ? String(contextResponse[key])
                                    : 'Not provided';

                                  return {
                                    variable: (
                                      <span className="font-mono text-sm bg-neutral-100 px-2 py-1 rounded">
                                        {key}
                                      </span>
                                    ),
                                    available: (
                                      <div className="flex justify-center">
                                        {isAvailable ? (
                                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-success text-neutral-0">
                                            ✓ Available
                                          </span>
                                        ) : (
                                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-error text-neutral-100">
                                            ✗ Missing
                                          </span>
                                        )}
                                      </div>
                                    ),
                                    responseValue: (
                                      <span
                                        className={`font-mono text-sm ${
                                          isAvailable
                                            ? 'text-viridian-700'
                                            : 'text-neutral-500'
                                        }`}
                                      >
                                        {responseValue}
                                      </span>
                                    ),
                                  };
                                })}
                                totalItems={Object.keys(contextObject).length}
                                currentPage={1}
                                onPageChange={() => {}}
                                onSort={() => {}}
                                loading={false}
                                disablePagination={true}
                                fullWidth={true}
                              />
                            </div>
                            <div className="text-sm text-neutral-600 mt-2">
                              <p>
                                This table shows which context variables
                                referenced in your agent's prompts are available
                                in the context endpoint response and their
                                corresponding values.
                              </p>
                            </div>
                          </div>
                        )}
                      </>
                    ),
                  },
                ]
              : []),
          ]}
        />
      )}
    </>
  );
};
